import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiShield, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { useWallet } from '../context/WalletContext';
import '../styles/ConfirmPhrase.css';

const ConfirmPhrasePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedWords, setSelectedWords] = useState([]);
  const [shuffledWords, setShuffledWords] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { createWallet } = useWallet();

  const mnemonicRef = useRef(location.state?.mnemonic || sessionStorage.getItem('temp_mnemonic') || '');
  const originalWordsRef = useRef(mnemonicRef.current.split(' ').slice(0, 12));

  useEffect(() => {
    if (!mnemonicRef.current) {
      navigate('/create-wallet');
      return;
    }
    setShuffledWords([...originalWordsRef.current].sort(() => Math.random() - 0.5));
  }, [navigate]);

  const handleWordClick = (word) => {
    if (selectedWords.includes(word)) {
      setSelectedWords(selectedWords.filter(w => w !== word));
      setError('');
    } else {
      setSelectedWords([...selectedWords, word]);
      setError('');
    }
  };

  const isCorrectOrder = () => selectedWords.join(' ') === originalWordsRef.current.join(' ');

  const handleConfirm = async () => {
    if (selectedWords.length !== originalWordsRef.current.length) {
      setError('Please select all 12 words');
      return;
    }

    if (!isCorrectOrder()) {
      setError('Incorrect word order. Please try again.');
      setSelectedWords([]);
      return;
    }

    try {
      setLoading(true);
      const twelveWordMnemonic = selectedWords.join(' ');
      await createWallet(twelveWordMnemonic);
      sessionStorage.removeItem('temp_mnemonic');
      navigate('/set-pin');
    } catch (error) {
      setError(error.message || 'Failed to create wallet.');
      setSelectedWords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="aurora-bg">
        <div className="aurora-blob aurora-2" />
        <div className="aurora-blob aurora-1" />
        <div className="grid-overlay" />
      </div>

      <motion.div
        className="content-wrapper"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-nav">
          <button className="nav-back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>
          <h1 className="page-title">Verify Phrase</h1>
        </div>

        <p className="page-subtitle">
          Tap the words in the correct order to verify your recovery phrase.
        </p>

        <div className="glass-card">
          <div className={`selected-area ${error ? 'error-pulse' : ''}`}>
            {originalWordsRef.current.map((_, index) => (
              <div
                key={index}
                className={`slot-chip ${selectedWords[index] ? 'filled' : 'empty'}`}
              >
                <span className="slot-num">{index + 1}</span>
                {selectedWords[index] && (
                  <motion.span
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                  >
                    {selectedWords[index]}
                  </motion.span>
                )}
              </div>
            ))}
          </div>

          <div className="divider-line" />

          {shuffledWords.length > 0 && (
            <motion.div
              className="word-bank"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.02 } }
              }}
            >
              {shuffledWords.map((word, index) => (
                <motion.button
                  key={`${word}-${index}`}
                  className={`bank-chip ${selectedWords.includes(word) ? 'used' : ''}`}
                  onClick={() => handleWordClick(word)}
                  disabled={loading || selectedWords.includes(word)}
                  whileTap={{ scale: 0.95 }}
                  variants={{
                    hidden: { opacity: 0, scale: 0.8 },
                    visible: { opacity: 1, scale: 1 }
                  }}
                >
                  {word}
                </motion.button>
              ))}
            </motion.div>
          )}

          {error && (
            <motion.div
              className="error-feedback"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FiShield /> {error}
            </motion.div>
          )}
        </div>

        <div className="action-footer">
          <button
            className="crystal-button btn-primary"
            onClick={handleConfirm}
            disabled={selectedWords.length !== originalWordsRef.current.length || loading}
            style={{
              opacity: selectedWords.length === originalWordsRef.current.length ? 1 : 0.5
            }}
          >
            {loading ? 'Verifying...' : 'Confirm'}
            {!loading && <FiCheck />}
            <div className="btn-shine" />
          </button>
        </div>

      </motion.div>
    </div>
  );
};

export default ConfirmPhrasePage;